// NPM Imports
import React, { useContext } from 'react'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
// Asset Imports
import Button from 'react-bootstrap/Button'
import { formatFloatAsCurrencySymbol } from '../../../common/helpers.mjs'

function EINIntegrationsTable({ einIndex, einIntegrations }) {
   const state = useQuoteContext()
   const { getString, getFormattedString } = state

   /**
    * Changes the integration type of the integration at the given index
    *
    * @param {String} value New integration type
    * @param {Integer} integrationIndex Index of the integration to change
    */
   function onIntegrationTypeChange(value, integrationIndex) {
      // copy the integration given the integration index
      const integration = { ...einIntegrations[integrationIndex] }
      //updates the integration type
      integration.integrationType = value
      // Update state
      state.onIntegrationChange(einIndex, integrationIndex, integration)
   }

   /**
    * Changes the provider type of the integration at the given index
    *
    * @param {String} value New Provider type
    * @param {Integer} integrationIndex Index of the integration to change
    */
   function onProviderChange(value, integrationIndex) {
      // copy the integration given the integration index
      const integration = { ...einIntegrations[integrationIndex] }
      //updates the provider
      integration.solutionCode = value
      //updates the provider price
      integration.solutionCost = Number(
         getProviderPrice(integration.solutionCode, true),
      )
      // Update state
      state.onIntegrationChange(einIndex, integrationIndex, integration)
   }

   /**
    * Returns a list of integration options excluding the current integration. That way the user can't select the same integration twice.
    *
    * @param {Integer} integrationIndex Index of the integration to get options for
    * @returns A list of integration options excluding the current integration
    */
   function getIntegrationOptions(integrationIndex) {
      // grabs all the integration types from the state
      const integrationTypeSelect = state.integrationTypes
      // gets the selected integration types excluding the current one
      const selectedTypes = einIntegrations
         .filter((_, i) => i !== integrationIndex)
         .map((integration) => integration.integrationType)
      // filters out the selected integration types

      const filteredOptions = integrationTypeSelect.filter(
         (option) => !selectedTypes.includes(option.integration_code),
      )
      const noneOption = { integration_code: 'None', name: 'None' }
      // If None is already selected somewhere, add it to the top of the list
      if (selectedTypes.includes('None')) filteredOptions.unshift(noneOption)
      return filteredOptions
   }

   /**
    * Returns a list of provider options for the given integration type
    *
    * @param {String} integrationType Integration type to get provider options for
    * @returns A list of provider options for the given integration type
    */
   function getProviderOptions(integrationType) {
      if (integrationType === 'None')
         return [{ solution_code: 'None', solution_name: 'None' }]
      // grabs all the providers from the state
      const providerSelect = Array.isArray(state.integrationSolutions)
         ? state.integrationSolutions.filter(
              (solution) =>
                 solution.type_code === integrationType ||
                 solution.solution_code === 'None',
           )
         : []
      return providerSelect
   }

   /**
    * Returns the price of the provider with the given code
    *
    * @param {String} providerCode Code of the provider to get the price for
    * @param {Boolean} rawPrice If true, returns the raw price without formatting
    * @returns The price of the provider with the given code
    */
   function getProviderPrice(providerCode, rawPrice = false) {
      if (rawPrice)
         return Array.isArray(state.integrationSolutions)
            ? state.integrationSolutions.find(
                 (solution) => solution.solution_code === providerCode,
              ).solution_price_integration
            : 0
      if (providerCode === 'None')
         return formatFloatAsCurrencySymbol('0', state.currencyObjectUSD, true)
      const price = Array.isArray(state.integrationSolutions)
         ? state.integrationSolutions.find(
              (solution) => solution.solution_code === providerCode,
           ).solution_price_integration
         : 0
      return formatFloatAsCurrencySymbol(price, state.currencyObjectUSD)
   }

   // Component for the EIN Integration Table Row
   function EINIntegrationTableRow({ integrationIndex }) {
      return (
         <tr>
            <td>
               {getString('product.EINIntegrationTable.Row.IntType')}
               <select
                  className='form-select-bhr2 integration-dropdown'
                  value={einIntegrations[integrationIndex].integrationType}
                  onChange={(e) =>
                     onIntegrationTypeChange(e.target.value, integrationIndex)
                  }
               >
                  {getIntegrationOptions(integrationIndex).map((e, key) => {
                     return (
                        <option key={key} value={e.integration_code}>
                           {e.name}
                        </option>
                     )
                  })}
               </select>
            </td>
            <td>
               {getString('product.EINIntegrationTable.Row.IntProvider')}
               <select
                  className='form-select-bhr2 integration-dropdown'
                  value={einIntegrations[integrationIndex].solutionCode}
                  onChange={(e) =>
                     onProviderChange(e.target.value, integrationIndex)
                  }
                  disabled={
                     einIntegrations[integrationIndex].integrationType ===
                     'None'
                  }
               >
                  {getProviderOptions(
                     einIntegrations[integrationIndex].integrationType,
                  ).map((e, key) => {
                     return (
                        <option key={key} value={e.solution_code}>
                           {e.solution_name}
                        </option>
                     )
                  })}
               </select>
            </td>
            <td>
               {getFormattedString('product.EINIntegrationTable.Row.IntCost', {
                  integrationCost: getProviderPrice(
                     einIntegrations[integrationIndex].solutionCode,
                  ),
               })}
            </td>
            <td>
               <Button
                  variant='danger'
                  size='sm'
                  onClick={(e) =>
                     state.onProductEINIntegrationRemove(
                        einIndex,
                        integrationIndex,
                     )
                  }
               >
                  {getString('global.Remove')}
               </Button>
            </td>
         </tr>
      )
   }

   return (
      <div style={{ padding: '0 20%' }}>
         <table className='table table-sm'>
            <tbody>
               {
                  /*Uses the number of EIN Count to create MultiEINTableRow*/
                  Array.from(
                     { length: einIntegrations.length },
                     (_, i) => i,
                  ).map((i) => (
                     <EINIntegrationTableRow key={i} integrationIndex={i} />
                  ))
               }
            </tbody>
         </table>
      </div>
   )
}

export default EINIntegrationsTable
